import { useMemo, useState } from 'react';
import { navigate, replaceRoute, resetNav } from '../../app/navigation';
import type { V2Evidence } from '../../domain/evidence';
import { displayAddress } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { useStore } from '../../state/createStore';
import { evidenceOfInspection, evidenceStore } from '../../state/evidenceStore';
import { findingStore } from '../../state/findingStore';
import { inspectionStore } from '../../state/inspectionStore';
import { Button, Card, EmptyState, InlineMessage, SectionHeader, TopBar } from '../../ui/components';
import { useStickyFooterClearance } from '../../ui/stickyFooter';
import { Lightbox } from '../evidence/Lightbox';
import { PhotoNoteSheet } from '../evidence/PhotoNoteSheet';
import { Thumbnail } from '../evidence/PhotoStrip';
import { DeletePhotosConfirm, ImportProgressBar, usePoolImport } from './poolImport';

// Path A · Bulk import (task book §8): photos already taken (device library / files) go into the
// inspection's PHOTO POOL first. Importing itself never depends on AI. When the inspector is done,
// the processing choice follows (AI analyzes the whole pool · manual).
// Owner device test 2026-10-05: a progress bar with the percentage while the photos are stored, and
// every photo here can carry its note and be deleted.
export function BulkImportScreen(props: { inspectionId: string }) {
  const { t, tp } = useT();
  const footerRef = useStickyFooterClearance();
  const inspection = useStore(inspectionStore, (s) => s.inspections.find((i) => i.id === props.inspectionId) ?? null);
  const evidenceState = useStore(evidenceStore, (s) => s);
  const inspectionState = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const pool = useMemo(() => evidenceOfInspection(evidenceState, props.inspectionId), [evidenceState, props.inspectionId]);
  const importer = usePoolImport(props.inspectionId);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [noteFor, setNoteFor] = useState<V2Evidence | null>(null);
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);

  if (!inspection) {
    return (
      <div className="screen">
        <TopBar title={t('import.title')} onBack={() => resetNav({ screen: 'inspectionList' })} />
        <EmptyState title={t('inspection.notFoundTitle')} body={t('inspection.notFoundBody')} />
      </div>
    );
  }

  const busy = importer.busy;
  const liveLightbox: V2Evidence[] = pool;
  const linkedIds = new Set([...inspectionState.items.flatMap((i) => i.evidenceIds), ...findingState.findings.flatMap((f) => f.evidenceIds)]);

  return (
    <div className="screen" data-testid="bulk-import-screen">
      <TopBar title={t('import.title')} subtitle={displayAddress(inspection, t('inspection.noAddress'))} onBack={() => resetNav({ screen: 'inspectionList' })} />
      <div className="screen-body">
        <Card tone="low" className="import-intro" testId="import-intro">
          <SectionHeader eyebrow={t('import.eyebrow')} title={t('import.heading')} />
          <p className="card-body">{t('import.body')}</p>
          <p className="card-note">{t('import.noAiNote')}</p>
        </Card>
        <div className="screen-cta">
          <Button icon="upload" onClick={() => void importer.run()} disabled={busy} testId="import-pick">
            {busy ? t('photo.importing') : pool.length > 0 ? t('import.pickMore') : t('import.pick')}
          </Button>
          {importer.message && !busy ? <InlineMessage kind={importer.message.kind} text={importer.message.text} /> : null}
        </div>
        <Card testId="import-pool">
          <SectionHeader title={t('pool.title')} meta={tp('common.photosCount', pool.length)} />
          {pool.length === 0 ? (
            <p className="card-note" data-testid="import-empty">
              {t('import.empty')}
            </p>
          ) : (
            <div className="photo-row" data-testid="import-grid">
              {pool.map((ev, index) => (
                <Thumbnail key={ev.id} evidence={ev} onClick={() => setLightbox(index)} onEditNote={busy ? undefined : setNoteFor} onRemove={busy ? undefined : (e) => setDeleteIds([e.id])} />
              ))}
            </div>
          )}
        </Card>
      </div>
      <div ref={footerRef} className="sticky-actions" data-testid="import-actions">
        {/* While photos are being stored the footer is the status bar; the way on comes back when it is done. */}
        {importer.progress ? (
          <ImportProgressBar progress={importer.progress} />
        ) : (
          <>
            <Button trailingIcon="arrow_forward" onClick={() => navigate({ screen: 'importChoice', inspectionId: props.inspectionId })} disabled={pool.length === 0} testId="import-continue">
              {t('import.continue')}
            </Button>
            <Button variant="text" onClick={() => replaceRoute({ screen: 'inspection', inspectionId: props.inspectionId })} testId="import-skip">
              {t('import.skip')}
            </Button>
          </>
        )}
      </div>
      {lightbox !== null && liveLightbox[lightbox] ? (
        <Lightbox evidence={liveLightbox} index={lightbox} onIndexChange={setLightbox} onClose={() => setLightbox(null)} context={{ itemId: null, findingId: null }} />
      ) : null}
      {noteFor ? <PhotoNoteSheet evidence={evidenceState.evidence.find((e) => e.id === noteFor.id) ?? noteFor} onClose={() => setNoteFor(null)} /> : null}
      {deleteIds ? <DeletePhotosConfirm ids={deleteIds} linked={deleteIds.filter((id) => linkedIds.has(id)).length} onClose={() => setDeleteIds(null)} /> : null}
    </div>
  );
}
